/**
 * CombatModal.jsx - Full-screen Combat Encounter Modal
 *
 * Displays when player encounters an enemy in a dungeon.
 * Pre-combat phase: Player chooses to Fight or Flee, can use potions
 * Combat phase: Continuous health draining with DPS display
 *
 * CSS Prefix: cm- (Combat Modal)
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useGame } from '../../context/GameContext';
import './CombatModal.css';

// Generate unique ID for floating damage
let damageIdCounter = 0;
const generateDamageId = () => `dmg_${Date.now()}_${damageIdCounter++}`;

const CombatModal = ({ onVictory }) => {
  const {
    currentEnemy,
    playerCharacter,
    escapeCombat,
    startCombat,
    getPlayerStats,
    useConsumable,
    useDungeonPotion,
    currentDungeon,
    potionsUsedThisRun,
  } = useGame();

  // Pre-combat vs combat state
  const [combatStarted, setCombatStarted] = useState(false);

  // Local state for floating damage numbers
  const [floatingDamage, setFloatingDamage] = useState([]);
  const [showVictory, setShowVictory] = useState(false);
  const [lastEnemyHealth, setLastEnemyHealth] = useState(null);
  const [lastPlayerHealth, setLastPlayerHealth] = useState(null);
  const [isShaking, setIsShaking] = useState({ player: false, enemy: false });

  // DPS calculation state
  const [playerDps, setPlayerDps] = useState(0);
  const [enemyDps, setEnemyDps] = useState(0);
  const damageHistoryRef = useRef({ player: [], enemy: [] });

  // Message for consumable usage
  const [useMessage, setUseMessage] = useState(null);

  const playerStats = getPlayerStats?.() || { health: 100, attack: 10, defense: 5, attackSpeed: 1 };

  // Get consumables from inventory (potions, buffs, etc.)
  const consumables = useMemo(() => {
    if (!playerCharacter?.inventory) return [];
    return playerCharacter.inventory.filter(item => item?.usable);
  }, [playerCharacter?.inventory]);

  // Reset state when enemy changes (new encounter)
  useEffect(() => {
    if (currentEnemy) {
      setCombatStarted(false);
      setShowVictory(false);
      setFloatingDamage([]);
      setLastEnemyHealth(null);
      setLastPlayerHealth(null);
      damageHistoryRef.current = { player: [], enemy: [] };
      setPlayerDps(0);
      setEnemyDps(0);
      setUseMessage(null);
    }
  }, [currentEnemy?.id]);

  // Calculate DPS from recent damage (only during combat)
  useEffect(() => {
    if (!combatStarted) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const windowMs = 3000;

      damageHistoryRef.current.player = damageHistoryRef.current.player.filter(d => now - d.time < windowMs);
      damageHistoryRef.current.enemy = damageHistoryRef.current.enemy.filter(d => now - d.time < windowMs);

      const playerTotal = damageHistoryRef.current.player.reduce((sum, d) => sum + d.amount, 0);
      const enemyTotal = damageHistoryRef.current.enemy.reduce((sum, d) => sum + d.amount, 0);

      setPlayerDps(Math.round(playerTotal / (windowMs / 1000)));
      setEnemyDps(Math.round(enemyTotal / (windowMs / 1000)));
    }, 200);

    return () => clearInterval(interval);
  }, [combatStarted]);

  // Track health changes to spawn floating damage (only during combat)
  useEffect(() => {
    if (!currentEnemy || !combatStarted) return;

    const enemyHealth = currentEnemy.stats?.health || 0;
    const playerHealth = playerCharacter?.currentHealth || 0;
    const now = Date.now();

    // Enemy took damage
    if (lastEnemyHealth !== null && enemyHealth < lastEnemyHealth) {
      const damage = lastEnemyHealth - enemyHealth;
      const isCrit = damage > playerStats.attack * 1.5;

      damageHistoryRef.current.player.push({ amount: damage, time: now });

      setFloatingDamage(prev => [...prev, {
        id: generateDamageId(),
        value: damage,
        isCrit,
        isMiss: false,
        target: 'enemy',
        timestamp: now,
        offsetX: (Math.random() - 0.5) * 50,
      }]);

      if (isCrit || damage > currentEnemy.stats.maxHealth * 0.2) {
        setIsShaking(prev => ({ ...prev, enemy: true }));
        setTimeout(() => setIsShaking(prev => ({ ...prev, enemy: false })), 150);
      }
    }

    // Player took damage
    if (lastPlayerHealth !== null && playerHealth < lastPlayerHealth) {
      const damage = lastPlayerHealth - playerHealth;

      damageHistoryRef.current.enemy.push({ amount: damage, time: now });

      setFloatingDamage(prev => [...prev, {
        id: generateDamageId(),
        value: damage,
        isCrit: false,
        isMiss: false,
        target: 'player',
        timestamp: now,
        offsetX: (Math.random() - 0.5) * 50,
      }]);

      if (damage > playerStats.health * 0.15) {
        setIsShaking(prev => ({ ...prev, player: true }));
        setTimeout(() => setIsShaking(prev => ({ ...prev, player: false })), 150);
      }
    }

    setLastEnemyHealth(enemyHealth);
    setLastPlayerHealth(playerHealth);
  }, [currentEnemy?.stats?.health, playerCharacter?.currentHealth, combatStarted, playerStats.attack, playerStats.health]);

  // Clean up floating damage
  useEffect(() => {
    if (!combatStarted) return;
    const cleanup = setInterval(() => {
      const now = Date.now();
      setFloatingDamage(prev => prev.filter(d => now - d.timestamp < 1000));
    }, 100);
    return () => clearInterval(cleanup);
  }, [combatStarted]);

  // Detect victory
  useEffect(() => {
    if (currentEnemy && currentEnemy.stats?.health <= 0 && !showVictory && combatStarted) {
      setShowVictory(true);
      setTimeout(() => {
        onVictory?.();
      }, 1500); // 1.5s delay to show victory screen
    }
  }, [currentEnemy?.stats?.health, showVictory, onVictory, combatStarted]);

  // Clear use message after delay
  useEffect(() => {
    if (useMessage) {
      const timer = setTimeout(() => setUseMessage(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [useMessage]);

  // Handle fight - start combat
  const handleFight = useCallback(() => {
    setCombatStarted(true);
    startCombat?.();
  }, [startCombat]);

  // Handle flee
  const handleFlee = useCallback(() => {
    escapeCombat();
  }, [escapeCombat]);

  // Handle using a consumable
  const handleUseConsumable = useCallback((item) => {
    if (!item || combatStarted) return;

    // For health potions in dungeons/towers, use the capped function (3 per run)
    const isHealthPotion = item.usable && item.effect?.type === 'heal';
    const isInDungeonOrTower = !!currentDungeon;

    let result;
    if (isHealthPotion && isInDungeonOrTower) {
      result = useDungeonPotion(item);
      if (result?.success && result.potionsRemaining !== undefined) {
        setUseMessage({ type: 'success', text: `${result.message} (${result.potionsRemaining} left)` });
        return;
      }
    } else {
      result = useConsumable(item);
    }

    if (result?.success) {
      setUseMessage({ type: 'success', text: result.message || `Used ${item.name}!` });
    } else {
      setUseMessage({ type: 'error', text: result?.error || 'Could not use item' });
    }
  }, [useConsumable, useDungeonPotion, currentDungeon, combatStarted]);

  // Don't render if no enemy
  if (!currentEnemy || !playerCharacter?.isInCombat) {
    return null;
  }

  const enemyHealthPercent = (currentEnemy.stats.health / currentEnemy.stats.maxHealth) * 100;
  const playerHealthPercent = (playerCharacter.currentHealth / playerStats.health) * 100;

  // Determine if player is low HP (for warning)
  const isLowHp = playerHealthPercent < 50;
  const isCriticalHp = playerHealthPercent < 25;

  return (
    <div className="cm-overlay">
      <div className="cm-modal">
        {/* Header */}
        <div className="cm-header">
          <span className="cm-header-text">
            {combatStarted ? '⚔️ COMBAT' : '👁️ ENCOUNTER'}
          </span>
        </div>

        {/* Main Combat Area */}
        <div className={`cm-combat-area ${showVictory ? 'victory-fade' : ''}`}>
          {/* Enemy Section */}
          <div className={`cm-combatant cm-enemy ${isShaking.enemy ? 'shake' : ''}`}>
            <div className="cm-combatant-row">
              <div className="cm-combatant-icon-wrap">
                <span className="cm-combatant-icon">{currentEnemy.icon || '👹'}</span>
                {currentEnemy.isBoss && <span className="cm-boss-badge">BOSS</span>}
              </div>
              <div className="cm-combatant-info">
                <h3 className="cm-combatant-name">{currentEnemy.name}</h3>
                <span className="cm-combatant-level">Lv. {currentEnemy.level || '?'}</span>
              </div>
            </div>

            {/* Enemy HP Bar */}
            <div className="cm-hp-section">
              <div className="cm-hp-bar-container">
                <div className="cm-hp-bar-fill enemy" style={{ width: `${Math.max(0, enemyHealthPercent)}%` }} />
                <span className="cm-hp-text">{Math.max(0, Math.floor(currentEnemy.stats.health))} / {currentEnemy.stats.maxHealth}</span>
              </div>
            </div>

            {/* Enemy Stats */}
            <div className="cm-stats-row">
              <span className="cm-stat">⚔️ {currentEnemy.stats.attack}</span>
              <span className="cm-stat">🛡️ {currentEnemy.stats.defense}</span>
              <span className="cm-stat">⚡ {currentEnemy.stats.speed?.toFixed(1) || '1.0'}</span>
              {combatStarted && <span className="cm-stat cm-dps enemy">{enemyDps > 0 ? `−${enemyDps}/s` : '...'}</span>}
            </div>

            {/* Floating Damage */}
            {combatStarted && (
              <div className="cm-floating-damage-container">
                {floatingDamage.filter(d => d.target === 'enemy').map(d => (
                  <div key={d.id} className={`cm-floating-damage ${d.isCrit ? 'crit' : ''} dealt`} style={{ left: `calc(50% + ${d.offsetX}px)` }}>
                    {d.isCrit ? `${d.value}!` : `-${d.value}`}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* VS / DPS Flow */}
          <div className="cm-vs-section">
            {combatStarted ? (
              <>
                <span className="cm-dps-label player">{playerDps > 0 ? `${playerDps} DPS ▲` : '...'}</span>
                <span className="cm-vs">VS</span>
                <span className="cm-dps-label enemy">{enemyDps > 0 ? `▼ ${enemyDps} DPS` : '...'}</span>
              </>
            ) : (
              <span className="cm-vs">VS</span>
            )}
          </div>

          {/* Player Section */}
          <div className={`cm-combatant cm-player ${isShaking.player ? 'shake' : ''}`}>
            {/* Floating Damage */}
            {combatStarted && (
              <div className="cm-floating-damage-container top">
                {floatingDamage.filter(d => d.target === 'player').map(d => (
                  <div key={d.id} className="cm-floating-damage taken" style={{ left: `calc(50% + ${d.offsetX}px)` }}>
                    -{d.value}
                  </div>
                ))}
              </div>
            )}

            {/* Player Stats */}
            <div className="cm-stats-row">
              {combatStarted && <span className="cm-stat cm-dps player">{playerDps > 0 ? `+${playerDps}/s` : '...'}</span>}
              <span className="cm-stat">⚔️ {playerStats.attack}</span>
              <span className="cm-stat">🛡️ {playerStats.defense}</span>
              <span className="cm-stat">⚡ {playerStats.attackSpeed?.toFixed(1) || '1.0'}</span>
            </div>

            {/* Player HP Bar */}
            <div className="cm-hp-section">
              <div className={`cm-hp-bar-container ${isCriticalHp ? 'critical' : isLowHp ? 'low' : ''}`}>
                <div className="cm-hp-bar-fill player" style={{ width: `${Math.max(0, playerHealthPercent)}%` }} />
                <span className="cm-hp-text">{Math.max(0, Math.floor(playerCharacter.currentHealth))} / {playerStats.health}</span>
              </div>
            </div>

            <div className="cm-combatant-row">
              <div className="cm-combatant-icon-wrap">
                <span className="cm-combatant-icon">👑</span>
              </div>
              <div className="cm-combatant-info">
                <h3 className="cm-combatant-name">You</h3>
                <span className="cm-combatant-level">Lv. {playerCharacter.level || 1}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Pre-Combat: Consumables Section */}
        {!combatStarted && (
          <div className="cm-consumables-section">
            <div className="cm-consumables-header">
              <span className="cm-consumables-title">🎒 Quick Use</span>
              {currentDungeon && (
                <span className="cm-potion-count">🧪 {3 - (potionsUsedThisRun || 0)}/3</span>
              )}
              {consumables.length === 0 && <span className="cm-consumables-empty">No consumables</span>}
            </div>
            {consumables.length > 0 && (
              <div className="cm-consumables-grid">
                {consumables.slice(0, 8).map((item, index) => (
                  <button
                    key={item.instanceId || index}
                    className={`cm-consumable-btn ${item.effect?.type === 'heal' ? 'heal' : 'buff'}`}
                    onClick={() => handleUseConsumable(item)}
                    title={`${item.name}\n${item.effect?.type === 'heal' ? `Restore ${item.effect.value} HP` : item.description || ''}`}
                  >
                    <span className="cm-consumable-icon">{item.icon}</span>
                    <span className="cm-consumable-name">{item.name?.replace(/\s+/g, ' ').split(' ').slice(0, 2).join(' ')}</span>
                  </button>
                ))}
              </div>
            )}
            {useMessage && (
              <div className={`cm-use-message ${useMessage.type}`}>
                {useMessage.text}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className={`cm-actions ${showVictory ? 'victory-fade' : ''}`}>
          {!combatStarted ? (
            <>
              <button className="cm-btn fight" onClick={handleFight}>⚔️ Fight</button>
              {currentEnemy?.isBoss ? (
                <button className="cm-btn flee disabled" disabled title="Cannot flee from a boss!">
                  🚫 No Escape
                </button>
              ) : (
                <button className="cm-btn flee" onClick={handleFlee}>🏃 Flee</button>
              )}
            </>
          ) : (
            currentEnemy?.isBoss ? (
              <button className="cm-btn flee disabled" disabled title="Cannot flee from a boss!">
                🚫 No Escape
              </button>
            ) : (
              <button className="cm-btn flee" onClick={handleFlee} disabled={showVictory}>🏃 Flee (50%)</button>
            )
          )}
        </div>

        {/* Victory Overlay */}
        {showVictory && (
          <div className="cm-victory-overlay">
            <div className="cm-victory-content">
              <span className="cm-victory-icon">🏆</span>
              <h2 className="cm-victory-text">VICTORY!</h2>
              <p className="cm-victory-sub">{currentEnemy.name} defeated!</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CombatModal;
